import React, { useState } from 'react';
import { Plus, MapPin, Pencil, Trash2, X, Search, Crosshair, Info, Bell, ChevronDown, Check } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Card, FieldLabel, inputClass, textareaClass, SegmentedControl } from './walletUi';
import { WalletMapPicker, LatLng } from './WalletMapPicker';
interface Place {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  created: string;
}
interface LocationMessage {
  id: string;
  message: string;
  placeIds: string[];
}
const MAX_LOCATIONS = 10;
const initialPlaces: Place[] = [{
  id: 'p1',
  name: 'Cellar door',
  address: '120 Vineyard Road, Riddells Creek VIC 3431',
  lat: -37.430481,
  lng: 144.662384,
  created: '3 Nov 2026'
}, {
  id: 'p2',
  name: 'City tasting room',
  address: '45 Flinders Lane, Melbourne VIC 3000',
  lat: -37.815602,
  lng: 144.971204,
  created: '3 Nov 2026'
}];
const initialMessages: LocationMessage[] = [{
  id: 'm1',
  message: 'Welcome back! Show your pass at the counter to earn points on your visit.',
  placeIds: ['p1']
}];

// Prototype search results
const searchResults: (LatLng & {
  label: string;
})[] = [{
  label: 'Riddells Creek VIC, Australia',
  lat: -37.4636,
  lng: 144.6739
}, {
  label: 'Macedon VIC, Australia',
  lat: -37.4237,
  lng: 144.5629
}, {
  label: 'Melbourne VIC, Australia',
  lat: -37.8136,
  lng: 144.9631
}];
function Modal({
  title,
  onClose,
  children,
  footer,
  width = 640
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  width?: number;
}) {
  return <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} data-id="element-2439" data-mp="22abny5m4dwu4" />
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none" data-id="element-2440" data-mp="1xctmt1ecglwo">
        <div className="bg-white rounded-lg shadow-xl max-h-[92vh] flex flex-col pointer-events-auto" style={{
        width
      }} data-id="element-2441" data-mp="2us7l8wdrgifh">
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-2442" data-mp="2pzefc56k341t">
            <h2 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-2443" data-mp="o3xghe4hzgod">{title}</h2>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F1F5F9]" aria-label="Close" data-id="element-2444" data-mp="t1gtkapglneq">
              <X className="w-5 h-5 text-[#64748B]" data-id="element-2445" data-mp="ecszk8v9hlqw" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-2446" data-mp="jbnhl8len69y">{children}</div>
          <div className="px-6 py-4 border-t border-[#E2E8F0] flex justify-end gap-2" data-id="element-2447" data-mp="5cebx2utruk8">{footer}</div>
        </div>
      </div>
    </>;
}
function PlaceEditor({
  place,
  isNew,
  onCancel,
  onSave
}: {
  place: Place;
  isNew: boolean;
  onCancel: () => void;
  onSave: (p: Place) => void;
}) {
  const [name, setName] = useState(place.name);
  const [address, setAddress] = useState(place.address);
  const [mode, setMode] = useState<'map' | 'coords'>('map');
  const hasPin = !isNew || place.lat !== 0;
  const [pin, setPin] = useState<LatLng | null>(hasPin ? {
    lat: place.lat,
    lng: place.lng
  } : null);
  const [centre, setCentre] = useState<LatLng>(hasPin ? {
    lat: place.lat,
    lng: place.lng
  } : {
    lat: -37.4636,
    lng: 144.6739
  });
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [latText, setLatText] = useState(pin ? String(pin.lat) : '');
  const [lngText, setLngText] = useState(pin ? String(pin.lng) : '');
  const setPinBoth = (p: LatLng) => {
    setPin(p);
    setLatText(String(p.lat));
    setLngText(String(p.lng));
  };
  const coordsValid = !isNaN(parseFloat(latText)) && !isNaN(parseFloat(lngText)) && Math.abs(parseFloat(latText)) <= 90 && Math.abs(parseFloat(lngText)) <= 180;
  return <Modal title={isNew ? 'Add location' : 'Edit location'} onClose={onCancel} width={720} footer={<>
          <Button variant="ghost" onClick={onCancel} data-id="element-2449" data-mp="2dk11xoo6dgl2">
            Cancel
          </Button>
          <Button variant="primary" disabled={!name || !pin} onClick={() => onSave({
      ...place,
      name,
      address,
      lat: pin!.lat,
      lng: pin!.lng
    })} data-id="element-2450" data-mp="28lpu1jhfxg5h">
            {isNew ? 'Add location' : 'Save'}
          </Button>
        </>} data-id="element-2448" data-mp="6ucpivjdxa9y">
      <div data-id="element-2451" data-mp="14m1bua6146hd">
        <FieldLabel data-id="element-2452" data-mp="14pp4l73ngov1">Location name</FieldLabel>
        <input className={inputClass} placeholder="e.g. Cellar door" value={name} onChange={e => setName(e.target.value)} data-id="element-2453" data-mp="19m5ofp3tgh2b" />
      </div>
      <div data-id="element-2454" data-mp="1dnr0j88j0rbg">
        <FieldLabel hint="For your reference. The pin on the map is what the pass uses." data-id="element-2455" data-mp="1dnhhk4lwfkf9">Address (optional)</FieldLabel>
        <input className={inputClass} placeholder="e.g. 120 Vineyard Road, Riddells Creek" value={address} onChange={e => setAddress(e.target.value)} data-id="element-2456" data-mp="lx4dnv7igttv" />
      </div>

      <div data-id="element-2457" data-mp="px55chjmf1iv">
        <div className="mb-3" data-id="element-2458" data-mp="pxerfu1jooja">
          <SegmentedControl value={mode} onChange={setMode} options={[{
          value: 'map',
          label: 'Pick on the map'
        }, {
          value: 'coords',
          label: 'Enter coordinates'
        }]} data-id="element-2459" data-mp="up6c5ny0s01i" />
        </div>

        {mode === 'map' ? <div className="space-y-3" data-id="element-2460" data-mp="27kjnqw31q5w">
            <div className="flex gap-2 relative" data-id="element-2461" data-mp="3qv0b6ok1avv4">
              <div className="relative flex-1" data-id="element-2462" data-mp="3qtp7spui3q4j">
                <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2463" data-mp="3lvw3618uj46d" />
                <input className={`${inputClass} pl-9`} placeholder="Search for a place or address" value={query} onChange={e => {
              setQuery(e.target.value);
              setShowResults(e.target.value.length > 1);
            }} data-id="element-2464" data-mp="3hu26s55oomc0" />
                {showResults && <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E2E8F0] rounded-md shadow-lg z-10" data-id="element-2465" data-mp="37xfpfqckgdeq">
                    {searchResults.map(r => <button key={r.label} type="button" onClick={() => {
                setCentre({
                  lat: r.lat,
                  lng: r.lng
                });
                setQuery(r.label);
                setShowResults(false);
              }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC]" data-id="element-2466" data-mp="2y5jq6byquh8x">
                        <MapPin className="w-4 h-4 text-[#94A3B8]" data-id="element-2467" data-mp="2k8wibe5966o8" /> {r.label}
                      </button>)}
                  </div>}
              </div>
              <Button variant="secondary" iconLeft={<Crosshair className="w-4 h-4" data-id="element-2469" data-mp="2ffy6iezazzwh" />} onClick={() => {
            const here = {
              lat: -37.430481,
              lng: 144.662384
            };
            setCentre(here);
            setPinBoth(here);
          }} data-id="element-2468" data-mp="2fisntxn0t4lo">
                My location
              </Button>
            </div>
            <WalletMapPicker centre={centre} onCentreChange={setCentre} pin={pin} onPinChange={setPinBoth} data-id="element-2470" data-mp="2tbs4w42gb6vc" />
          </div> : <div className="grid grid-cols-2 gap-4" data-id="element-2471" data-mp="2jjw0ycdoomj7">
            <div data-id="element-2472" data-mp="2jj32dyovp3c5">
              <FieldLabel data-id="element-2473" data-mp="2jhjlc5veeo9b">Latitude</FieldLabel>
              <input className={inputClass} placeholder="-37.430481" value={latText} onChange={e => {
            setLatText(e.target.value);
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && !isNaN(parseFloat(lngText))) setPin({
              lat: v,
              lng: parseFloat(lngText)
            });
          }} data-id="element-2474" data-mp="2ohxl70muirw9" />
            </div>
            <div data-id="element-2475" data-mp="10ky5dtzqzsue">
              <FieldLabel data-id="element-2476" data-mp="10lgc5kk1dpgk">Longitude</FieldLabel>
              <input className={inputClass} placeholder="144.662384" value={lngText} onChange={e => {
            setLngText(e.target.value);
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && !isNaN(parseFloat(latText))) setPin({
              lat: parseFloat(latText),
              lng: v
            });
          }} data-id="element-2477" data-mp="10kofs7im7np8" />
            </div>
            {!coordsValid && (latText || lngText) && <p className="col-span-2 text-xs text-[#DC2626] m-0" data-id="element-2478" data-mp="ho6sq3epobzb">Enter a latitude between -90 and 90 and a longitude between -180 and 180.</p>}
          </div>}

        <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-md text-sm ${pin ? 'bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46]' : 'bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]'}`} data-id="element-2479" data-mp="rj702q94do12">
          <MapPin className="w-4 h-4" data-id="element-2480" data-mp="22ynaxry2rlrq" />
          {pin ? <span data-id="element-2481" data-mp="231rq1kd2w1x6">
              <span className="font-medium" data-id="element-2482" data-mp="d9vskcrrge2">Pin dropped:</span> {pin.lat}, {pin.lng}
            </span> : 'No pin yet. Click the map to drop one.'}
        </div>
      </div>
    </Modal>;
}
function MessageEditor({
  value,
  places,
  usedElsewhere,
  isNew,
  onCancel,
  onSave,
  onAddPlace
}: {
  value: LocationMessage;
  places: Place[];
  usedElsewhere: number;
  isNew: boolean;
  onCancel: () => void;
  onSave: (m: LocationMessage) => void;
  onAddPlace: () => void;
}) {
  const [message, setMessage] = useState(value.message);
  const [selected, setSelected] = useState<string[]>(value.placeIds);
  const [open, setOpen] = useState(false);
  const remaining = MAX_LOCATIONS - usedElsewhere - selected.length;
  return <Modal title={isNew ? 'Add location message' : 'Edit location message'} onClose={onCancel} footer={<>
          <Button variant="ghost" onClick={onCancel} data-id="element-2484" data-mp="2uu3mvpbrk8xw">
            Cancel
          </Button>
          <Button variant="primary" disabled={!message.trim() || selected.length === 0} onClick={() => onSave({
      ...value,
      message,
      placeIds: selected
    })} data-id="element-2485" data-mp="2zswpuntfchjt">
            {isNew ? 'Add' : 'Save'}
          </Button>
        </>} data-id="element-2483" data-mp="xzgz7qxc72ou">
      <div data-id="element-2486" data-mp="1c47j3rf1t04v">
        <FieldLabel hint="Shows on the lock screen when a member with the pass is near one of the locations below." data-id="element-2487" data-mp="184gmh72ubpsf">Message</FieldLabel>
        <textarea rows={3} className={textareaClass} placeholder="e.g. Welcome back! Show your pass to earn points on your visit." value={message} onChange={e => setMessage(e.target.value)} data-id="element-2488" data-mp="136f30l1i5ogf" />
      </div>
      <div data-id="element-2489" data-mp="1vv2vb0ko5e3h">
        <FieldLabel data-id="element-2490" data-mp="1vvched2lf13w">Locations</FieldLabel>
        <div className="relative" data-id="element-2491" data-mp="1quybp1ogs391">
          <button type="button" onClick={() => setOpen(!open)} className={`${inputClass} flex items-center justify-between text-left`} data-id="element-2492" data-mp="1qu5a0f4d43xr">
            <span className={selected.length ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-2493" data-mp="1lvlr6nl2f89m">
              {selected.length ? `${selected.length} selected` : 'Choose locations'}
            </span>
            <ChevronDown className="w-4 h-4 text-[#64748B]" data-id="element-2494" data-mp="af3tyl9n91lx" />
          </button>
          {open && <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E2E8F0] rounded-md shadow-lg z-10 py-1" data-id="element-2495" data-mp="g5oznamw82kn">
              {places.map(p => {
            const on = selected.includes(p.id);
            const disabled = !on && remaining <= 0;
            return <button key={p.id} type="button" disabled={disabled} onClick={() => setSelected(prev => on ? prev.filter(x => x !== p.id) : [...prev, p.id])} className="flex items-center gap-3 w-full text-left px-3 py-2 text-sm hover:bg-[#F8FAFC] disabled:opacity-50" data-id="element-2496" data-mp="l3gsq7z9xuqf">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-2497" data-mp="18tkro21ip6b3">
                      {on && <Check className="w-3 h-3 text-white" data-id="element-2498" data-mp="13v18g9eb68mf" />}
                    </span>
                    <span className="flex-1" data-id="element-2499" data-mp="3alqxhbfrj9hd">{p.name}</span>
                    <span className="text-xs text-[#94A3B8] font-mono" data-id="element-2500" data-mp="35me8ak1f5qbm">{p.lat.toFixed(4)}, {p.lng.toFixed(4)}</span>
                  </button>;
          })}
              <div className="h-px bg-[#E2E8F0] my-1" data-id="element-2501" data-mp="1l4ltkbcqpksx" />
              <button type="button" onClick={() => {
            setOpen(false);
            onAddPlace();
          }} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-[#0284C7] hover:bg-[#F0F9FF]" data-id="element-2502" data-mp="1q36kfk63qs4p">
                <Plus className="w-4 h-4" data-id="element-2503" data-mp="1gbsnnk5jc6f9" /> Add a new location
              </button>
            </div>}
        </div>
        <p className="text-xs text-[#94A3B8] mt-1.5 m-0" data-id="element-2504" data-mp="12c16jajq29x1">
          Up to {MAX_LOCATIONS} locations across all messages. You can add {Math.max(0, remaining)} more.
        </p>
        {selected.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-id="element-2505" data-mp="sgzea5u5grq8">
            {selected.map(id => {
          const p = places.find(x => x.id === id);
          if (!p) return null;
          return <span key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#ECFDF5] border border-[#A7F3D0] text-sm text-[#065F46]" data-id="element-2506" data-mp="2muh82al5ektp">
                  <MapPin className="w-3.5 h-3.5" data-id="element-2507" data-mp="2qatuk0x30190" /> {p.name}
                  <button type="button" onClick={() => setSelected(prev => prev.filter(x => x !== id))} aria-label={`Remove ${p.name}`} data-id="element-2508" data-mp="2qceoppfhhi6c">
                    <X className="w-3.5 h-3.5" data-id="element-2509" data-mp="2gcnr3evmmn21" />
                  </button>
                </span>;
        })}
          </div>}
      </div>
    </Modal>;
}
export function WalletLocationsTab({
  onToast
}: {
  onToast: (msg: string) => void;
}) {
  const [places, setPlaces] = useState<Place[]>(initialPlaces);
  const [messages, setMessages] = useState<LocationMessage[]>(initialMessages);
  const [editingPlace, setEditingPlace] = useState<Place | null>(null);
  const [editingMessage, setEditingMessage] = useState<LocationMessage | null>(null);
  const [pendingMessage, setPendingMessage] = useState<LocationMessage | null>(null);
  const [confirm, setConfirm] = useState<{
    kind: 'place' | 'message';
    id: string;
  } | null>(null);
  const used = messages.reduce((s, m) => s + m.placeIds.length, 0);
  const placeIsNew = !!editingPlace && !places.some(p => p.id === editingPlace.id);
  const messageIsNew = !!editingMessage && !messages.some(m => m.id === editingMessage.id);
  const newPlace = (): Place => ({
    id: `p${Date.now()}`,
    name: '',
    address: '',
    lat: 0,
    lng: 0,
    created: '4 Nov 2026'
  });
  return <div className="space-y-6" data-id="element-2510" data-mp="d5ey7g2caqkg">
      <Card title="Location messages" description="When a member with the pass is near one of your locations, the pass pops up on their lock screen with your message." action={<Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-2513" data-mp="m2z2kxvi12za" />} disabled={used >= MAX_LOCATIONS || places.length === 0} onClick={() => setEditingMessage({
      id: `m${Date.now()}`,
      message: '',
      placeIds: []
    })} data-id="element-2512" data-mp="m3r0wgghdet9">
            Add location message
          </Button>} data-id="element-2511" data-mp="d2aok363v2me">
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#F0F9FF] border border-[#BAE6FD] text-sm text-[#0369A1] mb-4" data-id="element-2514" data-mp="10v0y65qjd9ti">
          <Info className="w-4 h-4" data-id="element-2515" data-mp="15qyh8jt6ou9z" /> {MAX_LOCATIONS - used} of {MAX_LOCATIONS} locations available
        </div>
        <div className="space-y-3" data-id="element-2516" data-mp="22i3cg21vf5jy">
          {messages.map(m => <div key={m.id} className="flex items-start gap-3 p-4 border border-[#E2E8F0] rounded-lg" data-id="element-2517" data-mp="1xkjxezxcmoqy">
              <div className="w-9 h-9 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0" data-id="element-2518" data-mp="2c9pt4dwz9wu3">
                <Bell className="w-4 h-4 text-[#0284C7]" data-id="element-2519" data-mp="27by0fhoie6ou" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2520" data-mp="1jmd7c5yn5t1u">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-2521" data-mp="1emh263e7jvkp">{m.message}</div>
                <div className="flex flex-wrap gap-2 mt-2" data-id="element-2522" data-mp="1eolcb32p9qtf">
                  {m.placeIds.map(id => {
                const p = places.find(x => x.id === id);
                return p ? <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#ECFDF5] border border-[#A7F3D0] text-xs text-[#065F46]" data-id="element-2523" data-mp="1ojvdxpp6rw6o">
                        <MapPin className="w-3 h-3" data-id="element-2524" data-mp="300d272imqsi8" /> {p.name}
                      </span> : null;
              })}
                </div>
              </div>
              <Button variant="secondary" size="sm" iconLeft={<Pencil className="w-4 h-4" data-id="element-2526" data-mp="3ciyzzkxno6bz" />} onClick={() => setEditingMessage(m)} data-id="element-2525" data-mp="37i1ilakgzgbj">
                Edit
              </Button>
              <Button variant="secondary" size="sm" iconLeft={<Trash2 className="w-4 h-4" data-id="element-2528" data-mp="32k9qogjtru7c" />} onClick={() => setConfirm({
            kind: 'message',
            id: m.id
          })} data-id="element-2527" data-mp="2xmgl9pqcj484">
                Delete
              </Button>
            </div>)}
          {messages.length === 0 && <div className="text-sm text-[#64748B] text-center py-6" data-id="element-2529" data-mp="3v96wvlari0jq">No location messages yet.</div>}
        </div>
        <p className="text-xs text-[#94A3B8] mt-4 m-0" data-id="element-2530" data-mp="3gizpc0mam1xt">
          Applies to every tier. Lock screen messages show on iPhone. Android shows the pass as a suggestion nearby.
        </p>
      </Card>

      <Card title="Your locations" description="Save your cellar door and other venues once, then use them in location messages." action={<Button variant="secondary" iconLeft={<Plus className="w-4 h-4" data-id="element-2533" data-mp="rpradhbcol02" />} onClick={() => setEditingPlace(newPlace())} data-id="element-2532" data-mp="hvbz8r2keztu">
            Add location
          </Button>} data-id="element-2531" data-mp="d29d3404asno">
        <table className="w-full text-sm" data-id="element-2534" data-mp="mrzi2m6smwvc">
          <thead data-id="element-2535" data-mp="motr11eltkwz">
            <tr className="text-left text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px] border-b border-[#E2E8F0]" data-id="element-2536" data-mp="ipb7a8yirfvq">
              <th className="py-2 pr-4" data-id="element-2537" data-mp="dp6qsixm2l5i">Location</th>
              <th className="py-2 pr-4" data-id="element-2538" data-mp="dskr7lmpskab">Coordinates</th>
              <th className="py-2 pr-4" data-id="element-2539" data-mp="8rx5nfh98vnh">Used in</th>
              <th className="py-2 pr-4" data-id="element-2540" data-mp="40f3wp7eqr1g">Added</th>
              <th className="py-2" data-id="element-2541" data-mp="3zn8bedt8tb6" />
            </tr>
          </thead>
          <tbody data-id="element-2542" data-mp="3v681p4rc8dbd">
            {places.map(p => {
            const usedIn = messages.filter(m => m.placeIds.includes(p.id)).length;
            return <tr key={p.id} className="border-b border-[#F1F5F9]" data-id="element-2543" data-mp="3r6phkb7cc69l">
                  <td className="py-3 pr-4" data-id="element-2544" data-mp="3m506ivk4bsli">
                    <div className="font-medium text-[#0F172A]" data-id="element-2545" data-mp="3m84m0p31aarh">{p.name}</div>
                    {p.address && <div className="text-xs text-[#64748B]" data-id="element-2546" data-mp="3h91p5rx7jaul">{p.address}</div>}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-[#475569]" data-id="element-2547" data-mp="1wr0m8v6ygdle">{p.lat}, {p.lng}</td>
                  <td className="py-3 pr-4 text-[#475569]" data-id="element-2548" data-mp="21q3rogqukltw">{usedIn === 0 ? 'Not used' : `${usedIn} message${usedIn === 1 ? '' : 's'}`}</td>
                  <td className="py-3 pr-4 text-[#475569]" data-id="element-2549" data-mp="2ap7bjmxu959g">{p.created}</td>
                  <td className="py-3 text-right whitespace-nowrap" data-id="element-2550" data-mp="2amcr3vetrkg1">
                    <button onClick={() => setEditingPlace(p)} className="p-2 rounded-md hover:bg-[#F1F5F9]" aria-label={`Edit ${p.name}`} data-id="element-2551" data-mp="1dzzlq2q6s9zq">
                      <Pencil className="w-4 h-4 text-[#64748B]" data-id="element-2552" data-mp="1mzd099t63orb" />
                    </button>
                    <button onClick={() => setConfirm({
                  kind: 'place',
                  id: p.id
                })} className="p-2 rounded-md hover:bg-[#FEF2F2]" aria-label={`Delete ${p.name}`} data-id="element-2553" data-mp="1rpte2v0whce7">
                      <Trash2 className="w-4 h-4 text-[#EF4444]" data-id="element-2554" data-mp="z90i7647kbn0" />
                    </button>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
        {places.length === 0 && <div className="text-sm text-[#64748B] text-center py-6" data-id="element-2555" data-mp="3jt101v7q8aii">No locations yet. Add your cellar door to get started.</div>}
      </Card>

      {editingMessage && !editingPlace && <MessageEditor value={editingMessage} places={places} isNew={messageIsNew} usedElsewhere={used - (messages.find(m => m.id === editingMessage.id)?.placeIds.length || 0)} onCancel={() => setEditingMessage(null)} onAddPlace={() => {
      setPendingMessage(editingMessage);
      setEditingMessage(null);
      setEditingPlace(newPlace());
    }} onSave={m => {
      setMessages(prev => messageIsNew ? [...prev, m] : prev.map(x => x.id === m.id ? m : x));
      setEditingMessage(null);
      onToast(messageIsNew ? 'Location message added' : 'Location message saved');
    }} data-id="element-2556" data-mp="3tqou0ixiddzm" />}

      {editingPlace && <PlaceEditor place={editingPlace} isNew={placeIsNew} onCancel={() => {
      setEditingPlace(null);
      if (pendingMessage) {
        setEditingMessage(pendingMessage);
        setPendingMessage(null);
      }
    }} onSave={p => {
      setPlaces(prev => placeIsNew ? [...prev, p] : prev.map(x => x.id === p.id ? p : x));
      setEditingPlace(null);
      onToast(placeIsNew ? 'Location added' : 'Location saved');
      if (pendingMessage) {
        setEditingMessage({
          ...pendingMessage,
          placeIds: [...pendingMessage.placeIds, p.id]
        });
        setPendingMessage(null);
      }
    }} data-id="element-2557" data-mp="q9ykvv6mqktp" />}

      {confirm && (() => {
      const isPlace = confirm.kind === 'place';
      const place = places.find(p => p.id === confirm.id);
      const usedIn = isPlace ? messages.filter(m => m.placeIds.includes(confirm.id)).length : 0;
      return <Modal title={isPlace ? `Delete ${place?.name}?` : 'Delete this location message?'} onClose={() => setConfirm(null)} width={440} footer={<>
              <Button variant="ghost" size="sm" onClick={() => setConfirm(null)} data-id="element-2559" data-mp="c8jat0f6w0s4">
                Keep it
              </Button>
              <Button variant="danger" size="sm" onClick={() => {
          if (isPlace) {
            setPlaces(prev => prev.filter(p => p.id !== confirm.id));
            setMessages(prev => prev.map(m => ({
              ...m,
              placeIds: m.placeIds.filter(id => id !== confirm.id)
            })).filter(m => m.placeIds.length > 0));
          } else {
            setMessages(prev => prev.filter(m => m.id !== confirm.id));
          }
          setConfirm(null);
          onToast(isPlace ? 'Location deleted' : 'Location message deleted');
        }} data-id="element-2560" data-mp="bbt1r442arjk">
                Delete
              </Button>
            </>} data-id="element-2558" data-mp="l8xu5u8lf5sz">
            <p className="text-sm text-[#475569] m-0" data-id="element-2561" data-mp="kbg720zwwhhw">
              {isPlace ? usedIn > 0 ? `It's used in ${usedIn} location message${usedIn === 1 ? '' : 's'}. It will be removed from them, and any message left with no locations is deleted too.` : 'It isn\'t used in any location messages.' : 'Passes will stop popping up near its locations.'}
            </p>
          </Modal>;
    })()}
    </div>;
}